import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../../config/axios.config';
import { useReportService, useInvalidateReportService } from '../../../hooks/queries/useReportService';
import TanTdsCheckOutputComponent from '../output/TanTdsCheckOutputComponent';
import ConsentCheckbox from './ConsentCheckbox';
import ReportBanner from '../ReportBanner';

const QUARTERS = [
    { label: 'Q1 (Apr–Jun)', value: 'Q1' },
    { label: 'Q2 (Jul–Sep)', value: 'Q2' },
    { label: 'Q3 (Oct–Dec)', value: 'Q3' },
    { label: 'Q4 (Jan–Mar)', value: 'Q4' },
];
const RETURN_TYPES = [
    { label: 'Salary', value: 'salary' },
    { label: 'Non-Salary', value: 'non-salary' },
    { label: 'TCS', value: 'tcs' },
];

const TAN_REGEX = /^[A-Z]{4}\d{5}[A-Z]$/;
const PAN_REGEX = /^[A-Z]{5}\d{4}[A-Z]$/;
const YEAR_REGEX = /^\d{4}$/;

const TanTdsCheckInputComponent = () => {
    const { serviceId } = useParams();
    const toast = useRef(null);
    const submittingRef = useRef(false);

    const [formData, setFormData] = useState({
        tanNumber: '', panNumber: '', year: '', quarter: null, typeOfReturn: null, consent: false,
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    // #161: one shared, cached query instead of a per-component fetch on every
    // mount. Concurrent consumers are deduped into a single request and a
    // revisit is served from cache; the balance is invalidated after a report
    // runs (below), which is what keeps a cached credit figure correct.
    const {
        data: fetchedServiceDetails,
        isFetching: fetchingService,
        isError: serviceError,
    } = useReportService(serviceId);
    const invalidateReportService = useInvalidateReportService();
    const serviceDetails = serviceError
        ? { available_credits: 'N/A', credits_required: 'N/A' }
        : (fetchedServiceDetails || null);

    const handleField = (field, transform = (v) => v) => (e) => {
        const raw = e.target ? e.target.value : e.value;
        setFormData((prev) => ({ ...prev, [field]: transform(raw ?? '') }));
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
    };

    const handleConsentChange = (e) => {
        setFormData((prev) => ({ ...prev, consent: e.checked }));
        if (errors.consent) setErrors((prev) => ({ ...prev, consent: '' }));
    };

    const validate = () => {
        const next = {};
        if (!formData.tanNumber) next.tanNumber = 'TAN number is required';
        else if (!TAN_REGEX.test(formData.tanNumber)) next.tanNumber = 'Invalid TAN format';
        if (!formData.panNumber) next.panNumber = 'PAN number is required';
        else if (!PAN_REGEX.test(formData.panNumber)) next.panNumber = 'Invalid PAN format';
        if (!formData.year) next.year = 'Financial year is required';
        else if (!YEAR_REGEX.test(formData.year)) next.year = 'Year must be 4 digits (e.g., 2024)';
        if (!formData.quarter) next.quarter = 'Quarter is required';
        if (!formData.typeOfReturn) next.typeOfReturn = 'Return type is required';
        if (!formData.consent) next.consent = 'Please confirm consent before submitting';
        return next;
    };

    const submit = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.post('/surepass/tan/tds-check', {
                tan_number: formData.tanNumber,
                pan_number: formData.panNumber,
                year: formData.year,
                quarter: formData.quarter,
                type_of_return: formData.typeOfReturn,
                api_provider_service_id: parseInt(serviceId, 10),
            });
            if (!res.data.success) throw new Error(res.data.message || 'TDS check failed');
            setReportData(res.data);
            toast.current?.show({ severity: 'success', summary: 'TDS check complete', detail: 'Report ready below', life: 2000 });
            // Credits just changed. Invalidating updates every mounted consumer at
            // once and is ordered strictly after the deduction.
            invalidateReportService(serviceId);
        } catch (error) {
            console.error('TAN TDS check failed:', error?.message || 'Unknown error');
            const status = error.response?.status;
            const map = {
                500: 'Internal server error. Please try again later.',
                422: 'Please check the inputs and try again.',
                429: 'Too many requests. Please wait a minute before trying again.',
                400: 'Invalid input. Please verify and retry.',
                404: 'No TDS record found for these details.',
            };
            let detail = map[status] || error.response?.data?.message || error.message || 'TDS check failed';
            if (status === 402) {
                const c = error.response?.data || {};
                detail = `Insufficient credits. Required: ${c.required_credits ?? 'N/A'}, Available: ${c.current_balance ?? 'N/A'}`;
            }
            toast.current?.show({ severity: 'error', summary: 'TDS check failed', detail, life: 3500 });
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const next = validate();
        if (Object.keys(next).length > 0) {
            setErrors(next);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix the highlighted fields', life: 3000 });
            return;
        }
        setErrors({});
        submittingRef.current = true;
        const required = serviceDetails?.credits_required;
        const available = serviceDetails?.available_credits;
        if (typeof available === 'number' && typeof required === 'number' && available < required) {
            submittingRef.current = false;
            toast.current?.show({ severity: 'error', summary: 'Insufficient credits', detail: `Required: ${required}, Available: ${available}`, life: 3500 });
            return;
        }
        confirmDialog({
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">TAN:</span> <span className="text-900 font-medium">{formData.tanNumber}</span></div>
                    <div className="mb-2"><span className="text-600">PAN:</span> <span className="text-900 font-medium">{formData.panNumber}</span></div>
                    <div className="mb-2"><span className="text-600">FY:</span> <span className="text-900 font-medium">{formData.year} · {formData.quarter} · {formData.typeOfReturn}</span></div>
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="text-900 font-medium">{available ?? 'N/A'}</span></div>
                    <div><span className="text-600">Credits to deduct:</span> <span className="text-900 font-medium">{required ?? 'N/A'}</span></div>
                </div>
            ),
            header: 'Confirm TAN TDS check',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, check',
            rejectLabel: 'Cancel',
            accept: submit,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '450px' },
            draggable: false,
        });
    };

    const handleAnother = () => {
        setReportData(null);
        setFormData({ tanNumber: '', panNumber: '', year: '', quarter: null, typeOfReturn: null, consent: false });
        setErrors({});
    };

    const fieldClass = (field) => classNames('w-full p-inputtext-sm', { 'p-invalid': !!errors[field] });
    const fieldClassMono = (field) => classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!errors[field] });
    const errorTip = (field, helpId) => errors[field] && (
        <small id={helpId} className="p-error block mt-1" role="alert">
            <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />{errors[field]}
        </small>
    );

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="grid">
                <div className="col-12">
                    <ReportBanner
                        icon="pi-file"
                        title="TAN TDS Check"
                        subtitle="Verify TDS for a TAN+PAN by financial year, quarter and return type"
                        availableCredits={serviceDetails?.available_credits}
                        requiredCredits={serviceDetails?.credits_required}
                        loading={fetchingService}
                    />
                    {!reportData ? (
                        <Card>
                            <form onSubmit={handleSubmit} noValidate>
                                <div className="grid">
                                    <div className="field col-12 md:col-6 lg:col-3">
                                        <label htmlFor="tdsTan" className="block font-medium text-sm mb-2">
                                            TAN <span className="text-red-500">*</span>
                                        </label>
                                        <IconField iconPosition="left" className="w-full">
                                            <InputIcon className="pi pi-hashtag" />
                                            <InputText id="tdsTan" value={formData.tanNumber} onChange={handleField('tanNumber', (v) => v.toUpperCase().trim())}
                                                placeholder="AAAA12345A" maxLength={10} autoCapitalize="characters" spellCheck={false}
                                                disabled={loading} className={fieldClassMono('tanNumber')} aria-describedby="tdsTan-help" aria-invalid={!!errors.tanNumber} />
                                        </IconField>
                                        {errorTip('tanNumber', 'tdsTan-help')}
                                    </div>
                                    <div className="field col-12 md:col-6 lg:col-3">
                                        <label htmlFor="tdsPan" className="block font-medium text-sm mb-2">
                                            PAN <span className="text-red-500">*</span>
                                        </label>
                                        <IconField iconPosition="left" className="w-full">
                                            <InputIcon className="pi pi-id-card" />
                                            <InputText id="tdsPan" value={formData.panNumber} onChange={handleField('panNumber', (v) => v.toUpperCase().trim())}
                                                placeholder="ABCDE1234F" maxLength={10} autoCapitalize="characters" spellCheck={false}
                                                disabled={loading} className={fieldClassMono('panNumber')} aria-describedby="tdsPan-help" aria-invalid={!!errors.panNumber} />
                                        </IconField>
                                        {errorTip('panNumber', 'tdsPan-help')}
                                    </div>
                                    <div className="field col-12 md:col-4 lg:col-2">
                                        <label htmlFor="tdsYear" className="block font-medium text-sm mb-2">
                                            FY <span className="text-red-500">*</span>
                                        </label>
                                        <InputText id="tdsYear" value={formData.year} onChange={handleField('year', (v) => v.replace(/\D/g, '').slice(0, 4))}
                                            placeholder="2024" maxLength={4} inputMode="numeric"
                                            disabled={loading} className={fieldClassMono('year')} aria-describedby="tdsYear-help" aria-invalid={!!errors.year} />
                                        {errorTip('year', 'tdsYear-help')}
                                    </div>
                                    <div className="field col-12 md:col-4 lg:col-2">
                                        <label htmlFor="tdsQuarter" className="block font-medium text-sm mb-2">
                                            Quarter <span className="text-red-500">*</span>
                                        </label>
                                        <Dropdown id="tdsQuarter" value={formData.quarter} options={QUARTERS} optionLabel="label" optionValue="value"
                                            onChange={handleField('quarter')} placeholder="Select" disabled={loading}
                                            className={fieldClass('quarter')} aria-describedby="tdsQuarter-help" aria-invalid={!!errors.quarter} />
                                        {errorTip('quarter', 'tdsQuarter-help')}
                                    </div>
                                    <div className="field col-12 md:col-4 lg:col-2">
                                        <label htmlFor="tdsType" className="block font-medium text-sm mb-2">
                                            Return Type <span className="text-red-500">*</span>
                                        </label>
                                        <Dropdown id="tdsType" value={formData.typeOfReturn} options={RETURN_TYPES} optionLabel="label" optionValue="value"
                                            onChange={handleField('typeOfReturn')} placeholder="Select" disabled={loading}
                                            className={fieldClass('typeOfReturn')} aria-describedby="tdsType-help" aria-invalid={!!errors.typeOfReturn} />
                                        {errorTip('typeOfReturn', 'tdsType-help')}
                                    </div>
                                </div>
                                <ConsentCheckbox checked={formData.consent} onChange={handleConsentChange} error={errors.consent} reportType="tan-tds-check" />
                                <div className="flex justify-content-end mt-3">
                                    <Button type="submit" label="Run TDS Check" icon="pi pi-arrow-right" iconPos="right" loading={loading} disabled={loading} size="small" />
                                </div>
                            </form>
                        </Card>
                    ) : (
                        <>
                            <div className="flex justify-content-end mb-3">
                                <Button label="New check" icon="pi pi-refresh" severity="secondary" outlined size="small" onClick={handleAnother} />
                            </div>
                            <TanTdsCheckOutputComponent reportData={reportData} />
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default TanTdsCheckInputComponent;
